import React, { useState, useEffect } from 'react';

const NewBookingCalendar = ({ adventureId, onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch availability data when adventure changes
  useEffect(() => {
    if (adventureId) {
      fetchAvailability();
    } else {
      setAvailableDates([]);
    }
  }, [adventureId]);

  const fetchAvailability = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log(`Fetching availability for adventure: ${adventureId}`);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/availability/${adventureId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch availability: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Availability response:', data);
      
      if (data.dates && Array.isArray(data.dates)) {
        setAvailableDates(data.dates);
      } else {
        setAvailableDates([]);
      }
      
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Failed to load availability');
      setAvailableDates([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate calendar days
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  // Navigate between months
  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  // Format date to YYYY-MM-DD in local timezone (Australia/Sydney)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get availability info for a specific date
  const getDateAvailability = (date) => {
    if (!date) return null;
    
    const dateStr = formatDateLocal(date);
    return availableDates.find(avail => avail.date === dateStr);
  };

  // Check if date is in the past
  const isPastDate = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Handle date click
  const handleDateClick = (date) => {
    if (!date || isPastDate(date)) return;
    
    const dateAvailability = getDateAvailability(date);
    if (!dateAvailability || dateAvailability.available_spots <= 0) return;
    
    const dateStr = formatDateLocal(date);
    onDateSelect(dateStr);
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const days = getDaysInMonth(currentMonth);

  if (!adventureId) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="text-center py-8 text-slate-500">
          <div className="text-6xl mb-4">🗓️</div>
          <p className="text-lg font-medium">Select an Adventure</p>
          <p className="text-sm">Choose an adventure above to see available dates</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500 mb-4"></div>
          <p className="text-slate-600">Loading available dates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="text-center py-8">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">{error}</p>
          <button 
            onClick={fetchAvailability}
            className="mt-3 px-4 py-2 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h3 className="text-xl font-semibold text-slate-800">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day Names Header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1 mb-6">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-16"></div>;
          }

          const dateAvailability = getDateAvailability(date);
          const isSelected = selectedDate === formatDateLocal(date);
          const isPast = isPastDate(date);
          const hasAvailability = dateAvailability && dateAvailability.available_spots > 0;
          
          let dayClass = "h-16 p-1 border rounded-lg cursor-pointer transition-all text-center flex flex-col justify-center text-xs";
          
          if (isPast) {
            dayClass += " bg-slate-100 text-slate-400 cursor-not-allowed";
          } else if (isSelected) {
            dayClass += " bg-coral-500 text-white border-coral-500 shadow-lg";
          } else if (hasAvailability) {
            dayClass += " bg-green-50 border-green-200 hover:bg-green-100 text-green-800 hover:shadow-md";
          } else {
            dayClass += " bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed";
          }

          return (
            <div
              key={index}
              className={dayClass}
              onClick={() => handleDateClick(date)}
            >
              <div className="font-medium">
                {date.getDate()}
              </div>
              
              {hasAvailability && !isPast && (
                <div className="text-[10px] leading-tight mt-1">
                  <div className="font-semibold">
                    {dateAvailability.available_spots} spots
                  </div>
                  {dateAvailability.price_override && (
                    <div className="text-orange-600 font-medium">
                      ${dateAvailability.price_override}
                    </div>
                  )}
                </div>
              )}
              
              {isPast && (
                <div className="text-[10px] text-slate-400">
                  Past
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Available Dates Summary */}
      {availableDates.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-amber-500 mr-3">ℹ️</div>
            <div>
              <p className="text-amber-800 font-medium">No availability set</p>
              <p className="text-amber-700 text-sm">
                The admin hasn't configured available dates for this adventure yet.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs mb-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-coral-500 rounded"></div>
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-slate-100 rounded"></div>
              <span>Unavailable</span>
            </div>
          </div>

          {/* Selected Date Info */}
          {selectedDate && (
            <div className="bg-coral-50 border border-coral-200 rounded-lg p-4">
              <div className="font-medium text-coral-800">
                Selected: {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-AU', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              {(() => {
                const selectedDateInfo = availableDates.find(d => d.date === selectedDate);
                if (selectedDateInfo) {
                  return (
                    <div className="text-sm text-coral-700 mt-2">
                      <div>{selectedDateInfo.available_spots} spots available</div>
                      {selectedDateInfo.price_override && (
                        <div className="font-medium text-orange-600 mt-1">
                          Special price: ${selectedDateInfo.price_override}
                        </div>
                      )}
                      {selectedDateInfo.notes && (
                        <div className="mt-1 text-slate-600 italic">
                          Note: {selectedDateInfo.notes}
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NewBookingCalendar;